import { t } from "@lingui/core/macro";
import type { ReactNode } from "react";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { cn } from "@/utils";
import type {
  CompensationDraft,
  CompensationMode,
} from "./timeOffCompensation";

/**
 * The type's own rate: the same select as on a time off, minus "Kot pri vrsti".
 * `children` sits beside the value input, so a date can share its row.
 */
export const CompensationRateFields = ({
  draft,
  onChange,
  error,
  children,
}: {
  draft: CompensationDraft;
  onChange: (draft: CompensationDraft) => void;
  error?: string | null;
  children?: ReactNode;
}) => {
  const hasValue = draft.mode !== "none";

  return (
    <div className="flex w-full flex-col gap-2">
      <LimeSelect
        label={t`Nadomestilo`}
        data-identifier="time-off-type-compensation-mode"
        disallowEmptySelection
        items={[
          { key: "none", label: t`Kot delovni čas` },
          { key: "percentOfHourlyCost", label: t`% urnega stroška` },
          { key: "flatPerDay", label: t`Znesek na dan` },
        ]}
        value={draft.mode}
        onSingleItemChange={(mode) =>
          onChange({ ...draft, mode: mode as CompensationMode })
        }
      />
      <div
        className={cn(
          "grid w-full gap-2",
          hasValue ? "grid-cols-2" : "grid-cols-1",
        )}
      >
        {draft.mode === "percentOfHourlyCost" && (
          <LimeInput
            label={t`Odstotek`}
            value={draft.percent}
            onValueChange={(percent) => onChange({ ...draft, percent })}
            inputMode="decimal"
            endContent={<span className="text-default-400 text-small">%</span>}
            error={error ?? undefined}
          />
        )}
        {draft.mode === "flatPerDay" && (
          <LimeInput
            label={t`Na dan`}
            placeholder="10,00"
            value={draft.flatAmount}
            onValueChange={(flatAmount) => onChange({ ...draft, flatAmount })}
            inputMode="decimal"
            endContent={<span className="text-default-400 text-small">€</span>}
            error={error ?? undefined}
          />
        )}
        {children}
      </div>
    </div>
  );
};
